import React from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { media } from '@atlaskit/primitives/responsive';
import { Heading, Image, Text } from '@atlassian/learning-components';

import { getAssetUrl } from 'src/services/http/user';

interface HeadingSectionProps {
  title: string;
  subtitle: string;
}

// Utility function to render title with "Rovo" in bold
const renderTitleWithBoldRovo = (title: string): string | (string | React.JSX.Element)[] => {
  if (!title.includes('Rovo')) {
    return title;
  }

  const parts = title.split(/(Rovo)/gi);

  return parts.map((part, index) => {
    if (part.toLowerCase() === 'rovo') {
      return (
        <span id="title-rovo-keyword" key={index} style={{ fontWeight: 700 }}>
          {part}
        </span>
      );
    }

    return part;
  });
};

const PageHeading: React.FC<HeadingSectionProps> = ({ title, subtitle }) => (
  <div
    css={headerContainerStyles}
    style={
      {
        '--rovo-flag': `url(${getAssetUrl('/images/hubs/rovoFlag.svg')})`,
        '--rovo-banner': `url(${getAssetUrl('/images/hubs/rovoHubBannerDesktop.svg')})`,
      } as Record<string, string>
    }
  >
    <div css={paddedContainerStyles}>
      <div css={rovoLogoStyles}>
        <Image src={getAssetUrl('/images/logos/rovo.svg')} alt="Rovo logo" />
      </div>
      <Heading id="rovo-adoption-hub" level={0} fontWeight={600}>
        {renderTitleWithBoldRovo(title)}
      </Heading>
      <div css={subtitleContainerStyles}>
        <Text size="large">{subtitle}</Text>
      </div>
    </div>
    <div css={rovoHubBannerMobileStyles}>
      <Image src={getAssetUrl('/images/hubs/rovoHubBannerMobile.svg')} alt="Rovo" role="presentation" />
    </div>
  </div>
);

const headerContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  marginTop: token('space.1000'),

  backgroundImage: `var(--rovo-banner)`,
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',

  '& #title-rovo-keyword': {
    position: 'relative',
  },

  '& #title-rovo-keyword::after': {
    content: '""',
    display: 'inline-block',
    width: '161px',
    height: '101px',
    backgroundImage: `var(--rovo-flag)`,
    backgroundSize: 'contain',
    position: 'absolute',
    right: '0',
    bottom: '0',
    transform: 'translateX(calc(100% - 30px))',
  },

  [media.below.md]: {
    marginTop: token('space.600'),

    '& #title-rovo-keyword::after': {
      display: 'none',
    },
  },

  [media.below.sm]: {
    backgroundImage: 'none',
  },
});

const subtitleContainerStyles = css({
  maxWidth: '536px',
});

const rovoHubBannerMobileStyles = css({
  display: 'none',

  [media.below.sm]: {
    display: 'block',
    width: '100%',
  },
});

const paddedContainerStyles = css({
  padding: `0 ${token('space.200')}`,

  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: token('space.200'),
  justifyContent: 'center',
  textAlign: 'center',
  maxWidth: '652px',
});

const rovoLogoStyles = css({
  display: 'flex',
  width: '48px',
  height: '48px',
  padding: '6px',
  justifyContent: 'center',
  alignItems: 'center',

  borderRadius: '12px',
  background: token('color.background.brand.bold'),
});

export default PageHeading;
